"use client";

import { useEffect } from "react";
import {
  capacitorPlugin,
  closeNativeBrowser,
  isNativeAndroid,
  navigateToStudioPath,
  resolveStudioPath,
} from "@/studio/lib/capacitorBridge";
import {
  configureNativeKeyboard,
  getNativeNetworkStatus,
  listenNativeNetwork,
  registerNativePush,
  setPendingFcmToken,
} from "@/studio/lib/nativeShell";

function applyNativeOfflineFlag(connected: boolean) {
  try {
    document.documentElement.dataset.studioNativeOffline = connected ? "0" : "1";
  } catch {
    /* ignore */
  }
}

/**
 * Capacitor Android: deep-link / App Link routing + close Wam Custom Tab on return.
 * Also mounts keyboard, network, and FCM wake listeners once.
 * Mounted from root layout so it runs even when StudioShell is not yet hydrated.
 */
export function NativeAppLinksRuntime() {
  useEffect(() => {
    if (!isNativeAndroid()) return;

    const removers: Array<() => void | Promise<void>> = [];

    const handleOpen = async (rawUrl: string | undefined | null) => {
      const path = resolveStudioPath(rawUrl);
      if (!path) return;
      // Returning from Wam (or any Studio App Link) — dismiss the Custom Tab.
      await closeNativeBrowser();
      navigateToStudioPath(rawUrl);
      window.dispatchEvent(
        new CustomEvent("studio:native-app-url", { detail: { url: rawUrl, path } }),
      );
    };

    const app = capacitorPlugin("App");
    if (app?.addListener) {
      void app
        .addListener("appUrlOpen", (event) => {
          void handleOpen(String(event.url ?? ""));
        })
        .then((handle) => removers.push(() => handle.remove()));
    }

    void app
      ?.getLaunchUrl?.()
      .then((result) => {
        const launch = result as { url?: string } | undefined;
        if (launch?.url) void handleOpen(launch.url);
      })
      .catch(() => {});

    const browser = capacitorPlugin("Browser");
    if (browser?.addListener) {
      void browser
        .addListener("browserFinished", () => {
          window.dispatchEvent(new CustomEvent("studio:native-browser-finished"));
        })
        .then((handle) => removers.push(() => handle.remove()));
    }

    void configureNativeKeyboard();

    void getNativeNetworkStatus().then((status) => {
      if (status) applyNativeOfflineFlag(status.connected);
    });
    removers.push(
      listenNativeNetwork((status) => {
        applyNativeOfflineFlag(status.connected);
      }),
    );

    void registerNativePush({
      onToken: (token) => {
        setPendingFcmToken(token);
      },
      onAction: (data) => {
        const path = typeof data.url === "string" ? data.url : null;
        if (path?.startsWith("/")) {
          navigateToStudioPath(`${window.location.origin}${path}`);
        }
      },
    }).then((removePush) => {
      removers.push(removePush);
    });

    return () => {
      for (const remove of removers) {
        try {
          void remove();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  return null;
}
